import {
  Alert,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { isEmpty, map } from 'lodash';
import React from 'react';

import {
  ThresholdConflictResolutions,
  ThresholdMergeState,
} from 'components/StudyDashboard/ProceduresPage/Actions/PostProcessingAndCalculateFeatures/thresholdMerge';

interface ThresholdMergePreviewProps {
  mergeState: ThresholdMergeState;
  setConflictResolutions: React.Dispatch<React.SetStateAction<ThresholdConflictResolutions>>;
}

const getInvalidJobMessage = (jobName?: string) =>
  `${jobName || 'Selected threshold job'} has no per-marker threshold results.`;

const ThresholdMergePreview = ({ mergeState, setConflictResolutions }: ThresholdMergePreviewProps) => {
  if (isEmpty(mergeState.previewRows) && isEmpty(mergeState.invalidJobs)) {
    return null;
  }

  return (
    <Grid container direction="column" spacing={1} sx={{ maxWidth: '100%', minWidth: 0 }}>
      <Grid item>
        <Typography variant="subtitle2">Merged threshold preview</Typography>
      </Grid>

      {map(mergeState.invalidJobs, (invalidJob) => (
        <Grid item key={`${invalidJob.jobId}-${invalidJob.reason}`}>
          <Alert severity="error">{getInvalidJobMessage(invalidJob.jobName || invalidJob.jobId)}</Alert>
        </Grid>
      ))}

      {!isEmpty(mergeState.unresolvedConflictMarkers) ? (
        <Grid item>
          <Alert severity="warning">
            Resolve duplicate marker thresholds for {mergeState.unresolvedConflictMarkers.join(', ')}.
          </Alert>
        </Grid>
      ) : null}

      {!isEmpty(mergeState.previewRows) ? (
        <Grid item>
          <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small" aria-label="Merged threshold preview">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ wordBreak: 'break-word' }}>Marker</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>Threshold</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>Source job</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {map(mergeState.previewRows, (row) => (
                  <TableRow key={row.marker}>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{row.marker}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{row.threshold ?? 'Select source'}</TableCell>
                    <TableCell sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                      {row.isConflict ? (
                        <RadioGroup
                          value={row.sourceJobId || ''}
                          onChange={(event) => {
                            const selectedJobId = event.target.value;
                            setConflictResolutions((currentResolutions) => ({
                              ...currentResolutions,
                              [row.marker]: selectedJobId,
                            }));
                          }}
                          name={`threshold-conflict-${row.marker}`}
                        >
                          {map(row.options, (option) => (
                            <FormControlLabel
                              key={option.jobId}
                              value={option.jobId}
                              control={<Radio size="small" />}
                              label={`${option.jobName || option.jobId}: ${option.threshold}`}
                              sx={{
                                alignItems: 'flex-start',
                                mr: 0,
                                '& .MuiFormControlLabel-label': { minWidth: 0, wordBreak: 'break-word' },
                              }}
                            />
                          ))}
                        </RadioGroup>
                      ) : (
                        row.sourceJobName || row.sourceJobId || 'Unknown job'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default ThresholdMergePreview;
